import { computed, Injectable, NgZone, inject, signal } from "@angular/core";
import { io, Socket } from "socket.io-client";
import { APP_CONSTANTS } from "../constants/app.constants";
import { ChatMessage, MatchFound } from "../models/chat.model";

export type MatchmakingState =
  "idle" | "searching" | "matched" | "reconnecting" | "ended";

export function acceptsMatchGeneration(
  current: number | null,
  incoming?: number,
): boolean {
  return incoming === undefined || current === null || incoming >= current;
}

@Injectable({ providedIn: "root" })
export class RealtimeService {
  private socket?: Socket;
  private readonly zone = inject(NgZone);
  readonly connected = signal(false);
  readonly state = signal<MatchmakingState>("idle");
  readonly searchGeneration = signal<number | null>(null);
  readonly matching = computed(() => this.state() === "searching");
  readonly matched = computed(() => this.state() === "matched");
  readonly roomId = signal<string | null>(null);
  readonly name = signal<string>(APP_CONSTANTS.defaultDisplayName);
  readonly avatarUrl = signal<string | null>(null);
  readonly country = signal<string>(APP_CONSTANTS.defaultCountry);
  readonly sharedInterests = signal<string[]>([]);
  readonly incomingMessage = signal<ChatMessage | null>(null);
  readonly peerSkipped = signal(false);
  readonly peerLeft = signal(false);
  readonly blockCompleted = signal(0);
  readonly id = signal<string | null>(null);
  readonly restored = signal(false);
  readonly restoreFailed = signal(false);
  readonly imageViewed = signal<string | null>(null);
  readonly peerIsTyping = signal(false);
  private typingTimer?: ReturnType<typeof setTimeout>;
  private presenceTimer?: ReturnType<typeof setInterval>;
  userId: string | null = null;

  connect(
    userId: string,
    displayName: string,
    restoreRoomId?: string | null,
    country?: string,
    avatarUrl?: string | null,
  ): void {
    this.userId = userId;
    this.incomingMessage.set(null);
    this.imageViewed.set(null);
    this.socket?.disconnect();
    this.socket = io(APP_CONSTANTS.socketUrl, {
      transports: ["websocket"],
      auth: {
        userId,
        displayName,
        avatarUrl: avatarUrl ?? "",
        country: country ?? "",
      },
    });
    this.socket.on("connect", () =>
      this.zone.run(() => {
        this.connected.set(true);
        this.restoreFailed.set(false);
        const roomToRestore = this.roomId() ?? restoreRoomId;
        if (
          roomToRestore &&
          (this.state() === "matched" || this.state() === "reconnecting")
        ) {
          this.socket?.emit(APP_CONSTANTS.socketEvents.match.restore, {
            roomId: roomToRestore,
          });
        }
      }),
    );
    this.socket.on("peer:typing", () =>
      this.zone.run(() => {
        this.peerIsTyping.set(true);
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(
          () => this.zone.run(() => this.peerIsTyping.set(false)),
          3000,
        );
      }),
    );
    this.socket.on(
      APP_CONSTANTS.socketEvents.match.restored,
      (event: MatchFound) =>
        this.zone.run(() => {
          if (!this.acceptGeneration(event.generation)) return;
          this.setMatchState(event, true);
          this.clearPeerTyping();
        }),
    );
    this.socket.on(APP_CONSTANTS.socketEvents.match.restoreFailed, () =>
      this.zone.run(() => {
        this.restored.set(false);
        this.restoreFailed.set(true);
        this.resetMatchState();
      }),
    );
    this.socket.on("disconnect", () =>
      this.zone.run(() => {
        this.connected.set(false);
        if (this.state() !== "idle" && this.state() !== "ended") {
          this.state.set("reconnecting");
        }
        this.name.set("Stranger");
        this.avatarUrl.set(null);
        this.country.set("");
        this.sharedInterests.set([]);
        this.clearPeerTyping();
      }),
    );
    this.socket.on(APP_CONSTANTS.socketEvents.match.peerSkipped, () =>
      this.zone.run(() => {
        this.resetMatchState();
        this.state.set("ended");
        this.peerSkipped.set(true);
        this.clearPeerTyping();
      }),
    );
    this.socket.on(APP_CONSTANTS.socketEvents.match.peerLeft, () =>
      this.zone.run(() => {
        this.resetMatchState();
        this.state.set("ended");
        this.peerLeft.set(true);
        this.clearPeerTyping();
      }),
    );
    this.socket.on(
      APP_CONSTANTS.socketEvents.match.waiting,
      (event: { generation?: number }) =>
        this.zone.run(() => {
          if (!this.acceptGeneration(event.generation)) return;
          this.state.set("searching");
        }),
    );
    this.socket.on(
      APP_CONSTANTS.socketEvents.match.found,
      (event: MatchFound) =>
        this.zone.run(() => {
          if (!this.acceptGeneration(event.generation)) return;
          this.setMatchState(event);
          this.clearPeerTyping();
        }),
    );
    this.socket.on(APP_CONSTANTS.socketEvents.match.skipped, () =>
      this.zone.run(() => {
        this.resetMatchState(true);
        this.clearPeerTyping();
      }),
    );
    this.socket.on(APP_CONSTANTS.socketEvents.match.cancelled, () =>
      this.zone.run(() => {
        this.resetMatchState();
        this.clearPeerTyping();
      }),
    );
    this.socket.on("moderation:completed", () =>
      this.zone.run(() => {
        this.resetMatchState(true);
        this.blockCompleted.update((count) => count + 1);
        this.clearPeerTyping();
      }),
    );
    this.socket.on(
      APP_CONSTANTS.socketEvents.match.profileUpdated,
      (data: { displayName: string; avatarUrl?: string }) =>
        this.zone.run(() => {
          this.name.set(data.displayName || APP_CONSTANTS.defaultDisplayName);
          this.avatarUrl.set(data.avatarUrl || null);
        }),
    );
    this.socket.on(
      APP_CONSTANTS.socketEvents.match.message,
      (message: ChatMessage) =>
        this.zone.run(() => {
          this.clearPeerTyping();
          this.incomingMessage.set(message);
          if (message.senderId !== this.userId) {
            this.socket?.emit(
              APP_CONSTANTS.socketEvents.match.messageReceived,
              { roomId: this.roomId(), messageId: message.id },
            );
          }
        }),
    );
    this.socket.on(APP_CONSTANTS.socketEvents.match.staleMatch, () =>
      this.zone.run(() => {
        this.resetMatchState();
        this.clearPeerTyping();
      }),
    );
    this.socket.on(
      APP_CONSTANTS.socketEvents.match.imageViewed,
      (data: { messageId: string }) =>
        this.zone.run(() => {
          this.imageViewed.set(data.messageId);
        }),
    );
  }

  join(interests: string[]): void {
    if (this.matching()) return;
    this.advanceExpectedGeneration();
    this.state.set("searching");
    this.peerSkipped.set(false);
    this.peerLeft.set(false);
    this.restored.set(false);
    this.socket?.emit(APP_CONSTANTS.socketEvents.match.join, { interests });
    clearInterval(this.presenceTimer);
    this.presenceTimer = setInterval(() => {
      if (this.state() === "searching") {
        this.socket?.emit(APP_CONSTANTS.socketEvents.match.heartbeat);
      }
    }, 10000);
  }

  cancelSearch(): void {
    this.advanceExpectedGeneration();
    this.state.set("idle");
    clearInterval(this.presenceTimer);
    this.roomId.set(null);
    this.socket?.emit(APP_CONSTANTS.socketEvents.match.cancel);
  }

  skip(interests: string[]): void {
    if (!this.matched()) return;
    this.advanceExpectedGeneration();
    const roomId = this.roomId();
    this.resetMatchState(true);
    this.socket?.emit(APP_CONSTANTS.socketEvents.match.skip, {
      roomId,
      interests,
    });
  }

  blockPeer(): void {
    const id = this.id();
    if (!id) return;
    this.advanceExpectedGeneration();
    this.socket?.emit(APP_CONSTANTS.socketEvents.match.block, {
      blockedUserId: id,
    });
  }

  excludePeer(): void {
    const id = this.id();
    if (id) {
      this.socket?.emit(APP_CONSTANTS.socketEvents.match.exclude, {
        otherUserId: id,
      });
    }
  }

  send(
    text: string,
    replyTo?: { id: string; text: string } | null,
    imageUrl?: string,
  ): void {
    const roomId = this.roomId();
    if (!roomId || !this.connected() || !this.matched()) return;
    const payload: Record<string, unknown> = {
      roomId,
      text,
      replyTo: replyTo ?? undefined,
    };
    if (imageUrl) {
      payload["imageUrl"] = imageUrl;
      payload["viewOnce"] = true;
    }
    this.socket?.emit(APP_CONSTANTS.socketEvents.match.message, payload);
  }

  emitTyping(): void {
    const roomId = this.roomId();
    if (!roomId || !this.connected() || !this.matched()) return;
    this.socket?.emit(APP_CONSTANTS.socketEvents.match.typing, { roomId });
  }

  notifyImageViewed(messageId: string): void {
    const roomId = this.roomId();
    if (!roomId || !this.connected() || !this.matched()) return;
    this.socket?.emit(APP_CONSTANTS.socketEvents.match.imageViewed, {
      roomId,
      messageId,
    });
  }

  updateProfile(displayName: string, avatarUrl: string | null): void {
    this.socket?.emit("profile:update", {
      displayName,
      avatarUrl: avatarUrl ?? "",
    });
  }

  hydrateMatch(state: {
    roomId: string;
    id: string | null;
    name: string;
    avatarUrl: string | null;
    country: string;
    sharedInterests: string[];
  }): void {
    this.setMatchState(state);
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = undefined;
    this.connected.set(false);
    this.resetMatchState();
    this.peerSkipped.set(false);
    this.peerLeft.set(false);
    this.restoreFailed.set(false);
    this.incomingMessage.set(null);
    this.imageViewed.set(null);
    this.clearPeerTyping();
  }

  private resetMatchState(matching = false): void {
    this.roomId.set(null);
    this.id.set(null);
    this.state.set(matching ? "searching" : "idle");
    if (!matching) clearInterval(this.presenceTimer);
    this.restored.set(false);
    this.name.set("Stranger");
    this.avatarUrl.set(null);
    this.country.set("");
    this.sharedInterests.set([]);
  }

  private setMatchState(
    state: {
      roomId: string;
      id: string | null;
      name?: string | null;
      avatarUrl?: string | null;
      country?: string;
      sharedInterests?: string[];
    },
    restored = false,
  ): void {
    this.roomId.set(state.roomId);
    this.id.set(state.id);
    this.name.set(state.name || APP_CONSTANTS.defaultDisplayName);
    this.avatarUrl.set(state.avatarUrl || null);
    this.country.set(state.country ?? "");
    this.sharedInterests.set(state.sharedInterests ?? []);
    this.peerSkipped.set(false);
    this.peerLeft.set(false);
    this.state.set("matched");
    this.restored.set(restored);
  }

  private acceptGeneration(generation?: number): boolean {
    if (generation === undefined) return true;
    const current = this.searchGeneration();
    if (!acceptsMatchGeneration(current, generation)) return false;
    this.searchGeneration.set(generation);
    return true;
  }

  private advanceExpectedGeneration(): void {
    this.searchGeneration.update((generation) =>
      generation === null ? 0 : generation + 1,
    );
  }

  private clearPeerTyping(): void {
    clearTimeout(this.typingTimer);
    this.peerIsTyping.set(false);
  }
}
